<template>
    <div>
        <div class="tw-flex tw-justify-center tw-mb-4">
            <div class="tw-w-full md:tw-w-2/3">
                <h3 class="tw-text-white">{{ __('purchase.workperks_website_title') }}</h3>
            </div>
        </div>

        <div class="tw-flex tw-justify-center">
            <div class="tw-w-full md:tw-w-2/3 tw-table">
                <form @submit="formWillSubmit">
                    <div class="tw-w-3/4 tw-p-0 tw-table-cell tw-align-bottom">
                        <input 
                            type="text"
                            class="tw-w-full tw-px-3 tw-py-2 tw-border tw-text-white tw-border-gray-300 tw-bg-transparent tw-rounded"
                            :placeholder="__('purchase.url_placeholder')"
                            v-model="url"
                            :class="{ 'tw-border-red-500': errors.url }" 
                        >
                    </div>
                    <div class="tw-w-1/4 tw-table-cell tw-align-bottom">
                        <div class="tw-px-3 tw-py-2">
                            <p class="tw-text-white tw-font-bold">.venngo.com</p>
                        </div>
                    </div>
                    <div class="tw-table-row tw-mt-1">
                        <p v-show="errors.url" class="tw-text-red-600">{{ errors.url }}</p>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, getCurrentInstance } from "vue";
import { useForm, useField } from "vee-validate";
import * as yup from "yup";

interface BusinessDetails {
    businessName?: string;
    [key: string]: any;
}

interface Props {
    currentBusinessDetails?: BusinessDetails;
    currentUrl?: string;
}

const props = withDefaults(defineProps<Props>(), {
    currentBusinessDetails: () => ({ businessName: "" }),
    currentUrl: "",
});

const emit = defineEmits<{
    "previous-component": [];
    "url-did-submit": [url: string];
}>();

const instance = getCurrentInstance();
const __ = instance?.appContext.config.globalProperties.__;

// Create validation schema
const validationSchema = yup.object({
    url: yup
        .string()
        .required(__('purchase.validation.required', { field: __('purchase.field_names.business_url') }))
        .min(3, __('purchase.validation.min_characters', { field: __('purchase.field_names.business_url'), min: 3 }))
        .max(15, __('purchase.validation.max_characters', { field: __('purchase.field_names.business_url'), max: 15 }))
        .matches(
            /^[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9]*$/,
            __('purchase.validation.alphanumeric_only', { field: __('purchase.field_names.business_url') })
        ),
});

// Setup form validation
const { handleSubmit, errors } = useForm({
    validationSchema,
    validateOnMount: false,
});

// Use field for the URL input
const { value: url } = useField("url");

// Initialize URL value on mount
onMounted(() => {
    if (props.currentUrl) {
        url.value = props.currentUrl;
    } else {
        url.value = props.currentBusinessDetails?.businessName?.toLowerCase().replace(/\s/g, '') || "";
    }
});

// Watch for changes to currentUrl prop
watch(() => props.currentUrl, (newUrl) => {
    url.value = newUrl;
});

// Form submission handler
const formWillSubmit = handleSubmit((values) => {
    emit("url-did-submit", values.url);
}, () => {
    // Handle validation errors - scroll to error
    const errorElement = document.querySelector(".tw-border-red-500");
    if (errorElement) {
        errorElement.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
});

// Expose methods for parent component access
const backBtnDidClick = () => {
    emit("previous-component");
};

defineExpose({
    backBtnDidClick,
    formWillSubmit,
});
</script>

